import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import ElipsisCell from '../../../../components/TableBox/Cells/EllipsisCell';
import Badge from '../../../../components/Badge';
import Button from '../../../../components/Button';
import {
    RowClassName,
    Modules,
    AuthRoutes,
    RouteParamsTypes,
    PermissionModulesEnum,
    PermissionActionsEnum,
} from '../../../../enums';
import { TableContext } from '../../../../contexts/TableContext';
import { AuthContext } from '../../../../contexts/AuthContext';
import { hasResourseAccess } from '../../../../utils/methods';
import { TemplateResponse } from '../../../../interfaces/template.interface';

interface RowProps {
    item: TemplateResponse;
    className?: RowClassName;
}

const Row: React.FC<RowProps> = ({ item, className }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { headCells } = useContext(TableContext);
    const { isSystemAdmin, permissions } = useContext(AuthContext);

    const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, {
        accessibleTo: [{ [PermissionModulesEnum.Templates]: [PermissionActionsEnum.Update] }],
    });

    const handleEditButtonClick = (id: string) => {
        navigate(`${Modules.templates}${AuthRoutes.templates}${RouteParamsTypes.edit}/${id}`);
    };

    return (
        <tr className={className}>
            {headCells.map((headCell) => {
                if (headCell.id === VALIDATION_FIELD_MAPPING.name) {
                    return <ElipsisCell key={headCell.id} width={headCell.width} text={item.name || '-'} />;
                }

                if (headCell.id === VALIDATION_FIELD_MAPPING.fileType) {
                    const text = typeof item.extension === 'string' ? item.extension : item.extension?.value || '-';
                    return (
                        <ElipsisCell key={headCell.id} width={headCell.width} text={text} />
                    );
                }

                if (headCell.id === VALIDATION_FIELD_MAPPING.description) {
                    return (
                        <ElipsisCell key={headCell.id} width={headCell.width} text={item.description || '-'} />
                    );
                }

                if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
                    return (
                        <td key={headCell.id} width={headCell.width}>
                            <Badge
                                text={
                                    item.is_active
                                        ? t('templates.list.pageTexts.table.status.active')
                                        : t('templates.list.pageTexts.table.status.inActive')
                                }
                                variant={item.is_active ? 'success' : 'danger'}
                            />
                        </td>
                    );
                }

                if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
                    return (
                        <td key={headCell.id} width={headCell.width}>
                            <div className="d-flex align-items-sm-center justify-content-sm-start">
                                {hasEditAccess && (
                                    <Button
                                        className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                                        icon={<i className="ri-edit-box-line ri-20px"></i>}
                                        onClick={() => handleEditButtonClick(item.id)}
                                    />
                                )}
                            </div>
                        </td>
                    );
                }

                const value = item[headCell.id as keyof TemplateResponse];
                return (
                    <ElipsisCell key={headCell.id} width={headCell.width} text={value ? String(value) : '-'} />
                );
            })}
        </tr>
    );
};

export default Row;
